<template>
  <div class="w-full col-span-full lg:col-span-1">
    <div class="relative">
      <button
        class="btn-filter-toggle btn is-outline w-full flex justify-between h-50 items-center"
        @click="isOpen = !isOpen"
      >
        <span class="text-xl">{{ $texts('search.filterText', 'Filter') }}</span>
        <ResponsiveSpriteSymbol
          :name="isOpen ? 'minus' : 'plus'"
          class="size-[14px] lg:size-20"
        />
      </button>

      <Transition name="slide-up">
        <div v-show="isOpen" class="search-sidebar">
          <div class="search-sidebar-inner">
            <button class="p-20 lg:hidden" @click="isOpen = false">
              <SpriteSymbol name="cross_mobile" class="size-15" />
            </button>

            <div class="p-20 lg:p-0">
              <template v-for="item in facetsAndFilters">
                <Facet
                  v-if="'facet' in item"
                  :key="item.key"
                  :label="item.facet.label"
                  :facet-key="item.facet.key"
                  :terms="item.facet.terms"
                />
                <RangeFilter
                  v-if="'filter' in item"
                  :key="item.key"
                  :label="item.filter.label"
                  :filter-key="item.filter.field"
                  :min="item.filter.min"
                  :max="item.filter.max"
                />
              </template>
            </div>

            <div
              class="flex justify-between md:justify-end mt-30 sticky bottom-0 p-20 bg-white lg:p-0"
            >
              <button
                class="btn block mr-10 flex-1 text-center md:hidden"
                @click="isOpen = false"
              >
                {{ $texts('search.showResultsText', 'Apply') }}
              </button>
              <nuxt-link
                :to="{ path: route.path }"
                class="btn flex-1 text-center"
                :class="{ disabled: !canReset }"
                >{{ $texts('resetText', 'Reset') }}</nuxt-link
              >
            </div>
          </div>
        </div>
      </Transition>

      <Transition name="search-sidebar-fade">
        <div
          v-if="isOpen"
          class="fixed top-0 left-0 size-full bg-black/75 z-40 lg:hidden"
        />
      </Transition>
    </div>

    <FilterChips :facets="facets" :filters="filters" />
  </div>
</template>

<script lang="ts" setup>
import { useScrollLock } from '@vueuse/core'
import type { ApiSearchFacet, ApiSearchFilterRange } from '../../types'

const isLocked = useScrollLock(import.meta.server ? null : document.body)

const isOpen = ref(false)
const { isMobileMenu } = useViewport()

watch(isOpen, (v) => {
  if (isMobileMenu.value) {
    isLocked.value = v
  } else {
    isLocked.value = false
  }
})

watch(isMobileMenu, (isMobileMenu) => {
  if (isMobileMenu) {
    isLocked.value = false
  }
})

const props = defineProps<{
  facets: ApiSearchFacet[]
  filters: ApiSearchFilterRange[]
  sorting: string[]
}>()

const route = useRoute()

const canReset = computed(
  () => !!Object.keys(route.query).filter((v) => v !== 'page').length,
)

/**
 * Merge facets and filters in one array so it can be sorted using the sorting
 * weights array.
 */
const facetsAndFilters = computed(() => {
  return [
    ...props.facets.map((facet) => {
      return {
        facet,
        key: facet.key,
        weight: props.sorting.indexOf(facet.key),
      }
    }),
    ...props.filters
      .filter((v) => v.field !== 'doc_count')
      .map((filter) => {
        return {
          filter,
          key: filter.key,
          weight: props.sorting.indexOf(filter.field),
        }
      }),
  ].sort((a, b) => {
    // Sort using the determined weights.
    return a.weight > b.weight ? 1 : -1
  })
})
</script>

<style lang="postcss">
.slide-up-enter-active,
.slide-up-leave-active {
  transition: 0.3s cubic-bezier(0.43, 0.72, 0.56, 0.99);
  > div {
    transition: inherit;
  }
  @screen lg {
    overflow: hidden;
  }
}
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);

  @screen lg {
    transform: none;
    opacity: 0;

    > div {
      transform: translateY(-100%);
    }
  }
}

.search-sidebar-fade-enter-active,
.search-sidebar-fade-leave-active {
  transition: 0.3s cubic-bezier(0.43, 0.72, 0.56, 0.99);
}
.search-sidebar-fade-enter-from,
.search-sidebar-fade-leave-to {
  opacity: 0;
}

.search-sidebar {
  @apply fixed top-0 left-0 w-full z-50 bg-white h-full until-lg:overflow-auto;

  @screen lg {
    @apply absolute top-full left-0 w-full z-40 h-auto bg-transparent;
    > div {
      @apply bg-white;
    }
  }
}
</style>
